import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { requestNotificationPermission } from '../utils/notifications';
import { identifyClarityUser } from '../utils/clarity';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Business Rule #48 — privileged sessions (staff/consultant/admin) die
  // server-side after 72h or when a newer login wins. Any API call then 401s;
  // this interceptor clears the dead token so route guards land on /login,
  // instead of every page failing quietly. Scoped to requests that carried
  // THIS session's token (a kiosk-login 401 or a wrong-OTP 401 must not log
  // the customer out).
  useEffect(() => {
    if (!token) return undefined;
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const sent = error?.config?.headers?.Authorization;
        const isLogoutCall = (error?.config?.url || '').endsWith('/auth/logout');
        if (error?.response?.status === 401 && sent === `Bearer ${token}` && !isLogoutCall) {
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Tie Clarity session recordings to the customer once authenticated. No-op off
  // production / before Clarity has initialised (see utils/clarity.js). Mirrors how
  // usePushNotifications links OneSignal on user.id change — covers every auth path
  // (login, OTP, signup, setAuth, and the mount fetchUser).
  useEffect(() => {
    if (user?.id) identifyClarityUser(user.id);
  }, [user?.id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      } else {
        console.error('Failed to fetch user:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    await requestNotificationPermission();
    return response.data;
  };

  const loginWithOTP = async (mobile, otp, extras = {}) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { mobile, otp, ...extras });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    await requestNotificationPermission();
    return response.data;
  };

  const signup = async (userData) => {
    const response = await axios.post(`${API}/auth/signup`, userData);
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    await requestNotificationPermission();
    return response.data;
  };

  const updateProfile = async (profileData) => {
    const response = await axios.put(`${API}/auth/profile`, profileData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(response.data);
    return response.data;
  };

  const logout = () => {
    const oldToken = token;
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    // Business Rule #48 — server-side logout: for staff/consultant/admin this
    // clears the single session so the token dies on EVERY device, not just
    // this one. Fire-and-forget (state is already cleared; a 401 here means
    // the session was already dead, which is the desired end state anyway).
    if (oldToken) {
      axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${oldToken}` }
      }).catch(() => {});
    }
  };

  // Direct auth setter for returning-customer auto-login (no OTP)
  const setAuth = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, loginWithOTP, signup, updateProfile, logout, loading, fetchUser, setAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
